import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useStaffInvitation } from '@/hooks/useStaffInvitation';
import { toast } from 'sonner';

interface UseAcceptInvitationStateReturn {
  handleAcceptInvitation: (e: React.FormEvent) => Promise<void>;
  invitationStatus: 'loading' | 'valid' | 'invalid';
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  confirmPassword: string;
  setConfirmPassword: (password: string) => void;
  firstName: string;
  setFirstName: (name: string) => void;
  lastName: string;
  setLastName: (name: string) => void;
  isRegistering: boolean;
  isLoading: boolean;
}

export const useAcceptInvitationState = (): UseAcceptInvitationStateReturn => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, signUp } = useAuth();
  const { activateInvitation, isLoading } = useStaffInvitation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [invitationStatus, setInvitationStatus] = useState<'loading' | 'valid' | 'invalid'>('loading');

  useEffect(() => {
    if (!token) {
      setInvitationStatus('invalid');
      return;
    }

    // Assume the invitation is valid for now
    setInvitationStatus('valid');
  }, [token]);

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Неверная ссылка приглашения');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }

    if (password.length < 8) {
      toast.error('Пароль должен содержать не менее 8 символов');
      return;
    }

    setIsRegistering(true);

    try {
      if (!user) {
        await signUp(email, password, {
          fullName: `${firstName} ${lastName}`,
        });

        setTimeout(async () => {
          const result = await activateInvitation(token, user?.id || '');
          if (result.success) {
            navigate('/main');
          }
        }, 2000);
      } else {
        const result = await activateInvitation(token, user.id);
        if (result.success) {
          navigate('/main');
        }
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
    } finally {
      setIsRegistering(false);
    }
  };

  return {
    handleAcceptInvitation,
    invitationStatus,
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    isRegistering,
    isLoading,
  };
};

